import { Chess } from 'chess.js';
import { Chessground as mountChessground } from '@lichess-org/chessground';
import {
  start as startBoardDrag,
  move as moveBoardDrag,
  end as endBoardDrag,
  cancel as cancelBoardDrag
} from '@lichess-org/chessground/drag';
import { getKeyAtDomPos, whitePov } from '@lichess-org/chessground/board';
import Swal from 'sweetalert2';

export { Chess, Swal };

/**
 * Creates a non-interactive preview with the exact same DOM contract used by
 * Chessground (`.cg-wrap piece.{color}.{role}`).  Piece-theme stylesheets can
 * therefore change once and affect both the board and every external preview
 * (for example the Position Editor palette) without a second icon set.
 */
export function createChessPiecePreview({ color, role }) {
  const preview = document.createElement('span');
  preview.className = 'cg-wrap chess-piece-preview';
  preview.dataset.pieceColor = color;
  preview.dataset.pieceRole = role;
  preview.setAttribute('aria-hidden', 'true');

  const piece = document.createElement('piece');
  piece.classList.add(color, role);
  preview.append(piece);
  return preview;
}

/**
 * The single mount point for every Web Chessground instance. A shared gesture
 * arbiter is installed only for coarse pointers, so every mobile board gets the
 * same scroll/tap/drag behavior while desktop Chessground remains untouched.
 */
export function createChessground(element, configuration) {
  element.classList.add('board-touch-scroll');
  element.dataset.boardTouchMode = configuration?.viewOnly === true ? 'viewer' : 'interactive';
  const ground = mountChessground(element, configuration);
  const removeTouchPolicy = installCoarseBoardTouchPolicy(element, ground, configuration);
  if (removeTouchPolicy) {
    const destroy = ground.destroy.bind(ground);
    ground.destroy = () => {
      removeTouchPolicy();
      destroy();
    };
  }
  return ground;
}

/**
 * Chessground normally owns every touch that starts over a piece. Mobile pages
 * still need native vertical scrolling when a gesture starts on an empty square
 * or on a piece that the viewer cannot move. The shared policy therefore has
 * two paths:
 *   - a playable viewer piece is selected on the first touchstart and that
 *     gesture belongs to Chessground immediately (Lichess-style tap/drag);
 *   - every other start remains pending, so a quick vertical swipe can pan the
 *     page while a tap or deliberate board gesture still reaches Chessground.
 */
function installCoarseBoardTouchPolicy(element, ground, configuration) {
  if (configuration?.viewOnly === true
      || typeof matchMedia !== 'function'
      || !matchMedia('(hover: none) and (pointer: coarse)').matches) return null;

  const dragHoldMilliseconds = 170;
  const intentDistance = 7;
  let gesture = null;
  let touchTargetFrame = 0;

  const isPlayableTouch = event => {
    const touch = event.touches[0];
    if (!touch) return false;
    const key = getKeyAtDomPos(
      [touch.clientX, touch.clientY],
      whitePov(ground.state),
      ground.state.dom.bounds()
    );
    return isTouchOwnedPiece(ground.state, key);
  };

  const syncTouchOwnedPieces = () => {
    cancelAnimationFrame(touchTargetFrame);
    touchTargetFrame = requestAnimationFrame(() => {
      if (ground.state.dom.destroyed) return;
      ground.state.dom.elements.board.querySelectorAll('piece').forEach(pieceElement => {
        pieceElement.classList.toggle(
          'board-touch-owned-piece',
          isTouchOwnedPiece(ground.state, pieceElement.cgKey)
        );
      });
    });
  };

  // Movable state changes after every server/engine move without necessarily
  // replacing piece nodes, so keep the touch-action marker in step with set().
  const setGroundConfiguration = ground.set.bind(ground);
  ground.set = nextConfiguration => {
    setGroundConfiguration(nextConfiguration);
    syncTouchOwnedPieces();
  };
  const boardMutations = new MutationObserver(syncTouchOwnedPieces);
  boardMutations.observe(ground.state.dom.elements.board, { childList: true });
  syncTouchOwnedPieces();

  const touchById = touches => {
    if (!gesture) return null;
    for (const touch of touches) {
      if (touch.identifier === gesture.identifier) return touch;
    }
    return null;
  };

  const onTouchStart = event => {
    if (event.touches.length !== 1) return;
    // This capture listener is on the shared wrap, before Chessground's target
    // listener on cg-board. Document move/end listeners remain intact.
    event.stopPropagation();
    const touch = event.touches[0];
    const ownsGesture = isPlayableTouch(event);
    gesture = {
      identifier: touch.identifier,
      x: touch.clientX,
      y: touch.clientY,
      startedAt: performance.now(),
      mode: ownsGesture ? 'board' : 'pending',
      startEvent: event
    };
    if (ownsGesture) {
      // A non-passive preventDefault on the initial event is what makes this
      // exact gesture non-scrollable; document listeners then cover movement
      // outside the piece, so Pointer Capture is unnecessary and would risk
      // changing Chessground's touchend origin-target contract.
      if (event.cancelable) event.preventDefault();
      startBoardDrag(ground.state, event);
    }
  };

  const onTouchMove = event => {
    if (!gesture) return;
    if (event.touches.length !== 1) {
      gesture.mode = 'scroll';
      cancelBoardDrag(ground.state);
      return;
    }
    const touch = touchById(event.touches);
    if (!touch) return;
    const deltaX = touch.clientX - gesture.x;
    const deltaY = touch.clientY - gesture.y;
    const distance = Math.hypot(deltaX, deltaY);
    if (gesture.mode === 'board') {
      if (event.cancelable) event.preventDefault();
      moveBoardDrag(ground.state, event);
      return;
    }
    if (gesture.mode === 'pending' && distance >= intentDistance) {
      const elapsed = performance.now() - gesture.startedAt;
      const quickVerticalSwipe = Math.abs(deltaY) > Math.abs(deltaX) * 1.2
        && elapsed < dragHoldMilliseconds;
      gesture.mode = quickVerticalSwipe ? 'scroll' : 'drag';
      if (quickVerticalSwipe) {
        cancelBoardDrag(ground.state);
      } else {
        startBoardDrag(ground.state, gesture.startEvent);
      }
    }
    if (gesture.mode === 'drag') {
      if (event.cancelable) event.preventDefault();
      moveBoardDrag(ground.state, event);
    }
  };

  const onTouchFinish = event => {
    if (!gesture) return;
    if (gesture.mode === 'scroll' || event.type === 'touchcancel') {
      cancelBoardDrag(ground.state);
    } else if (gesture.mode === 'board') {
      if (event.cancelable) event.preventDefault();
      endBoardDrag(ground.state, event);
    } else {
      if (event.cancelable) event.preventDefault();
      if (gesture.mode === 'pending') startBoardDrag(ground.state, gesture.startEvent);
      endBoardDrag(ground.state, event);
    }
    gesture = null;
  };

  const onScroll = () => {
    if (!gesture) return;
    gesture.mode = 'scroll';
    cancelBoardDrag(ground.state);
  };

  element.addEventListener('touchstart', onTouchStart, { capture: true, passive: false });
  document.addEventListener('touchmove', onTouchMove, { capture: true, passive: false });
  document.addEventListener('touchend', onTouchFinish, { capture: true, passive: false });
  document.addEventListener('touchcancel', onTouchFinish, { capture: true, passive: true });
  window.addEventListener('scroll', onScroll, { capture: true, passive: true });

  return () => {
    cancelAnimationFrame(touchTargetFrame);
    boardMutations.disconnect();
    element.removeEventListener('touchstart', onTouchStart, { capture: true });
    document.removeEventListener('touchmove', onTouchMove, { capture: true });
    document.removeEventListener('touchend', onTouchFinish, { capture: true });
    document.removeEventListener('touchcancel', onTouchFinish, { capture: true });
    window.removeEventListener('scroll', onScroll, { capture: true });
  };
}

function isTouchOwnedPiece(state, key) {
  if (!key || (!state.selectable.enabled && !state.draggable.enabled)) return false;
  const piece = state.pieces.get(key);
  if (!piece) return false;

  const movableNow = (state.movable.color === 'both'
      || (state.movable.color === piece.color && state.turnColor === piece.color))
    && (state.movable.free || !state.movable.dests || state.movable.dests.has(key));
  const premovableNow = state.premovable.enabled
    && state.movable.color === piece.color
    && state.turnColor !== piece.color;
  return movableNow || premovableNow;
}

export function legalDestinations(game) {
  const destinations = new Map();
  for (const square of game.board().flat().filter(Boolean).map(piece => piece.square)) {
    const moves = game.moves({ square, verbose: true });
    if (moves.length > 0) {
      destinations.set(square, moves.map(move => move.to));
    }
  }
  return destinations;
}

export function observeBoardResize(ground, element) {
  if (!ground || !element || typeof ResizeObserver === 'undefined') return null;

  let frame = 0;
  const redraw = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => ground.redrawAll());
  };

  const observer = new ResizeObserver(redraw);
  observer.observe(element);
  window.addEventListener('orientationchange', redraw, { passive: true });
  redraw();
  return observer;
}

export function createPlayableBoard(element, game, onMove, options = {}) {
  const ground = createChessground(element, {
    fen: game.fen(),
    orientation: options.orientation ?? 'white',
    turnColor: toColor(game.turn()),
    coordinates: true,
    animation: { enabled: true, duration: 180 },
    highlight: { lastMove: true, check: true },
    movable: {
      free: false,
      color: options.enabled === false && !options.premoveEnabled
        ? undefined
        : (options.movableColor ?? toColor(game.turn())),
      dests: options.enabled === false ? new Map() : legalDestinations(game),
      showDests: true,
      rookCastle: true,
      events: {
        after: async (from, to, metadata) => {
          // Some boards (notably post-game analysis) intentionally swap the
          // mutable position while keeping one Chessground instance alive.
          // Resolve it at gesture time so an experimental variation can never
          // mutate the authoritative game object retained by the online page.
          const activeGame = options.getGame?.() ?? game;
          const promotion = await (options.selectPromotion ? options.selectPromotion(activeGame, from, to) : selectPromotionIfNeeded(activeGame, from, to));
          const move = activeGame.move({ from, to, promotion: promotion ?? 'q' });
          if (!move) {
            refreshPlayableBoard(ground, activeGame, options.enabled !== false, undefined, options);
            return;
          }

          await onMove?.(move, ground, metadata);
          if (options.refreshAfterMove !== false) {
            refreshPlayableBoard(ground, activeGame, options.enabled !== false, [from, to], options);
          }
        }
      }
    },
    premovable: {
      enabled: options.premoveEnabled === true,
      showDests: true,
      castle: true,
      events: {
        set: (from, to) => options.onPremoveSet?.(from, to),
        unset: () => options.onPremoveUnset?.()
      }
    }
  });

  observeBoardResize(ground, element);
  return ground;
}

export function refreshPlayableBoard(ground, game, enabled = true, lastMove = undefined, options = {}) {
  const premoveEnabled = options.premoveEnabled === true && !game.isGameOver();
  ground.set({
    fen: game.fen(),
    turnColor: toColor(game.turn()),
    check: game.inCheck() ? toColor(game.turn()) : false,
    lastMove,
    movable: {
      color: (enabled || premoveEnabled) && !game.isGameOver()
        ? (options.movableColor ?? toColor(game.turn()))
        : undefined,
      dests: enabled && !game.isGameOver() ? legalDestinations(game) : new Map()
    },
    premovable: {
      enabled: premoveEnabled,
      showDests: true,
      castle: true,
      events: {
        set: (from, to) => options.onPremoveSet?.(from, to),
        unset: () => options.onPremoveUnset?.()
      }
    }
  });
}

export function createViewerBoard(element, fen, orientation = 'white') {
  const ground = createChessground(element, {
    fen,
    orientation,
    coordinates: true,
    viewOnly: true,
    animation: { enabled: true, duration: 180 },
    highlight: { lastMove: true, check: true },
    movable: { color: undefined, dests: new Map() }
  });

  observeBoardResize(ground, element);
  return ground;
}

export function setViewerPosition(ground, fen, lastMove = undefined) {
  ground.set({ fen, lastMove, movable: { color: undefined, dests: new Map() } });
}

export function renderMoveList(container, moves, activePly = moves.length, onSelect = null) {
  container.replaceChildren();
  if (moves.length === 0) {
    const placeholder = document.createElement('div');
    placeholder.className = 'move-placeholder';
    placeholder.textContent = 'هنوز حرکتی انجام نشده است';
    container.append(placeholder);
    return;
  }

  for (let index = 0; index < moves.length; index += 2) {
    const row = document.createElement('div');
    row.className = 'move-pair';
    const number = document.createElement('span');
    number.className = 'move-number';
    number.textContent = `${Math.floor(index / 2) + 1}.`;
    row.append(number);
    row.append(createMoveButton(moves[index], index + 1, activePly, onSelect));
    if (moves[index + 1]) {
      row.append(createMoveButton(moves[index + 1], index + 2, activePly, onSelect));
    } else {
      row.append(document.createElement('span'));
    }
    container.append(row);
  }

  const active = container.querySelector('.move-button.active');
  if (active) {
    scrollElementInsideContainer(container, active);
  } else {
    container.scrollTop = container.scrollHeight;
  }
}

export function formatClock(milliseconds, showHundredthsUnderTenSeconds = false) {
  const safeMilliseconds = Math.max(0, Number(milliseconds) || 0);
  if (showHundredthsUnderTenSeconds && safeMilliseconds < 10_000) {
    const totalCentiseconds = Math.floor(safeMilliseconds / 10);
    const minutes = Math.floor(totalCentiseconds / 6000);
    const seconds = Math.floor((totalCentiseconds % 6000) / 100);
    const centiseconds = totalCentiseconds % 100;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(centiseconds).padStart(2, '0')}`;
  }

  const safeSeconds = Math.max(0, Math.ceil(safeMilliseconds / 1000));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function copyText(text, successMessage = 'کپی شد') {
  return navigator.clipboard.writeText(text).then(() =>
    Swal.fire({ toast: true, position: 'bottom-start', icon: 'success', title: successMessage, showConfirmButton: false, timer: 1700 }));
}

export function gameResultText(game) {
  if (game.isCheckmate()) return game.turn() === 'w' ? 'کیش‌ومات؛ سیاه برنده شد' : 'کیش‌ومات؛ سفید برنده شد';
  if (game.isStalemate()) return 'پات؛ بازی مساوی شد';
  if (game.isThreefoldRepetition()) return 'تکرار سه‌باره؛ بازی مساوی شد';
  if (game.isInsufficientMaterial()) return 'مهره کافی برای مات وجود ندارد؛ تساوی';
  if (game.isDrawByFiftyMoves()) return 'قانون پنجاه حرکت؛ بازی مساوی شد';
  if (game.isDraw()) return 'بازی مساوی شد';
  if (game.inCheck()) return `کیش؛ نوبت ${game.turn() === 'w' ? 'سفید' : 'سیاه'}`;
  return `نوبت ${game.turn() === 'w' ? 'سفید' : 'سیاه'} است`;
}

function createMoveButton(move, ply, activePly, onSelect) {
  const button = document.createElement('button');
  button.type = 'button';
  const notation = typeof move === 'string' ? move : move.san;
  button.className = `move-button${ply === activePly ? ' active' : ''}${notation.includes('x') ? ' capture' : ''}${notation.includes('+') || notation.includes('#') ? ' check' : ''}`;
  button.textContent = notation;
  button.dataset.ply = String(ply);
  button.setAttribute('aria-label', `حرکت ${Math.ceil(ply / 2)} ${ply % 2 ? 'سفید' : 'سیاه'}: ${notation}`);
  if (onSelect) button.addEventListener('click', () => onSelect(ply));
  else button.disabled = true;
  return button;
}


function scrollElementInsideContainer(container, element) {
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const padding = 6;

  if (elementRect.top < containerRect.top + padding) {
    container.scrollTop -= (containerRect.top + padding) - elementRect.top;
  } else if (elementRect.bottom > containerRect.bottom - padding) {
    container.scrollTop += elementRect.bottom - (containerRect.bottom - padding);
  }
}

export async function selectPromotionIfNeeded(game, from, to) {
  const piece = game.get(from);
  const promotionRank = piece?.color === 'w' ? '8' : '1';
  if (piece?.type !== 'p' || !to?.endsWith(promotionRank)) return undefined;

  const color = piece.color === 'w' ? 'white' : 'black';
  let selected = 'q';
  await Swal.fire({
    title: 'ترفیع پیاده',
    html: `<div class="promotion-piece-grid cg-wrap" role="group" aria-label="انتخاب مهره ترفیع">
      <button type="button" data-promotion-piece="q" aria-label="وزیر"><piece class="queen ${color}"></piece></button>
      <button type="button" data-promotion-piece="r" aria-label="رخ"><piece class="rook ${color}"></piece></button>
      <button type="button" data-promotion-piece="b" aria-label="فیل"><piece class="bishop ${color}"></piece></button>
      <button type="button" data-promotion-piece="n" aria-label="اسب"><piece class="knight ${color}"></piece></button>
    </div>`,
    showConfirmButton: false,
    allowOutsideClick: false,
    allowEscapeKey: false,
    didOpen: popup => {
      popup.querySelectorAll('[data-promotion-piece]').forEach(button => {
        button.addEventListener('click', () => {
          selected = button.dataset.promotionPiece;
          Swal.close();
        });
      });
    }
  });
  return selected;
}

function toColor(turn) {
  return turn === 'w' ? 'white' : 'black';
}
